export interface ClaimPaymentOrderFilterOption {
  value: string;
  label: string;
  orderIds: string[];
  amount?: number;
  productId?: string | null;
  hasCollectiblePaymentPlanInterest?: boolean;
  refundHandling?: string | null;
  disputeHandling?: string | null;
}

export interface BuiltOrderFilterOption extends ClaimPaymentOrderFilterOption {
  hasActiveOrder: boolean;
  hasDefaultOrder: boolean;
}

export const formatOrderLabel = (orderId: string) => {
  return `Order ${orderId}`;
};

const formatSentenceFragment = (label: string) => {
  return label.charAt(0).toLowerCase() + label.slice(1);
};

export const formatEmptyPaymentScope = (option?: BuiltOrderFilterOption) => {
  if (!option) {
    return 'claim';
  }

  const firstOrderId = option.orderIds[0];
  if (firstOrderId && option.label === formatOrderLabel(firstOrderId)) {
    return 'order';
  }

  return formatSentenceFragment(option.label);
};

const dedupeOrderIds = (orderIds: string[]) => {
  return orderIds.filter((orderId, index) => orderId && orderIds.indexOf(orderId) === index);
};

export const buildOrderFilterOptions = (
  payments: Array<{ orderId: string }>,
  orderFilterOptions: ClaimPaymentOrderFilterOption[],
  activeOrderId?: string,
  defaultOrderId?: string,
): BuiltOrderFilterOption[] => {
  const seenOrderIds: { [orderId: string]: boolean } = {};
  const options: BuiltOrderFilterOption[] = [];

  const addOrderOption = ({
    value,
    label,
    orderIds,
    amount,
    productId,
    hasCollectiblePaymentPlanInterest,
    refundHandling,
    disputeHandling,
  }: ClaimPaymentOrderFilterOption) => {
    const uniqueOrderIds = dedupeOrderIds(orderIds);
    if (uniqueOrderIds.length === 0) {
      return;
    }

    uniqueOrderIds.forEach((orderId) => {
      seenOrderIds[orderId] = true;
    });
    const hasActiveOrder = !!(activeOrderId && uniqueOrderIds.includes(activeOrderId));
    const hasDefaultOrder = !!(defaultOrderId && uniqueOrderIds.includes(defaultOrderId));
    options.push({
      value,
      label,
      orderIds: uniqueOrderIds,
      ...(amount !== undefined ? { amount } : {}),
      ...(productId !== undefined ? { productId } : {}),
      ...(hasCollectiblePaymentPlanInterest !== undefined ? { hasCollectiblePaymentPlanInterest } : {}),
      ...(refundHandling !== undefined ? { refundHandling } : {}),
      ...(disputeHandling !== undefined ? { disputeHandling } : {}),
      hasActiveOrder,
      hasDefaultOrder,
    });
  };

  orderFilterOptions.forEach((option) => {
    addOrderOption(option);
  });
  payments.forEach((payment) => {
    if (!seenOrderIds[payment.orderId]) {
      addOrderOption({
        value: payment.orderId,
        label: formatOrderLabel(payment.orderId),
        orderIds: [payment.orderId],
      });
    }
  });

  if (activeOrderId && !seenOrderIds[activeOrderId]) {
    addOrderOption({ value: activeOrderId, label: formatOrderLabel(activeOrderId), orderIds: [activeOrderId] });
  }

  if (!activeOrderId && defaultOrderId && !seenOrderIds[defaultOrderId]) {
    addOrderOption({ value: defaultOrderId, label: formatOrderLabel(defaultOrderId), orderIds: [defaultOrderId] });
  }

  const activeOption = options.find((option) => option.hasActiveOrder);
  const defaultOption = options.find((option) => option.hasDefaultOrder);
  const inactiveOptions = options.filter((option) => !option.hasActiveOrder && !option.hasDefaultOrder);

  if (activeOption) {
    return [activeOption, ...inactiveOptions];
  }
  if (defaultOption) {
    return [defaultOption, ...inactiveOptions];
  }
  return options;
};
